import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Award, Gift, Star, Droplets, TrendingUp, Copy, Check, LogOut,
  Loader2, History, Wallet, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const LoyaltyPage = () => {
  const navigate = useNavigate();
  const { api, user, logout } = useAuth();
  const [copied, setCopied] = useState(false);
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLoyaltyData = async () => {
      try {
        const res = await api().get('/loyalty/points');
        setLoyaltyData(res.data);
      } catch (err) {
        console.error('Error loading loyalty data:', err);
      }
      setLoading(false);
    };
    loadLoyaltyData();
  }, [api]);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const copyCardId = () => {
    navigator.clipboard.writeText(user?.loyalty_card_id || '');
    setCopied(true);
    toast.success('ID do cartão copiado!');
    setTimeout(() => setCopied(false), 2000);
  };

  const rewards = [
    { points: 50, name: 'Desconto 5%', description: 'Na próxima compra', tier: 'Bronze' },
    { points: 100, name: 'Filtro de Óleo', description: 'Grátis na troca', tier: 'Bronze' },
    { points: 200, name: '1L Ngol Extra', description: 'Óleo grátis', tier: 'Prata' },
    { points: 500, name: 'Troca Completa', description: 'Óleo + mão de obra', tier: 'Ouro' },
  ];

  const currentPoints = loyaltyData?.total_points || user?.loyalty_points || 0;
  const currentTier = loyaltyData?.tier;
  const pointsToNextReward = rewards.find(r => r.points > currentPoints);

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-barlow font-bold text-xl">Fidelidade</h1>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Loyalty Card */}
        <div className="bg-gradient-to-br from-sonangol-red to-sonangol-orange rounded-2xl p-5 text-white shadow-lg relative overflow-hidden">
          {/* Background pattern */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          
          <div className="relative">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Droplets className="w-6 h-6" />
                  <span className="font-barlow font-bold text-xl uppercase tracking-tight">HáLubri</span>
                </div>
                <p className="text-white/70 text-xs">SONANGOL · Cartão de Fidelidade</p>
              </div>
              <Award className="w-10 h-10 text-white/30" />
            </div>

            <div className="mb-4">
              <p className="text-white/70 text-xs mb-1">Titular</p>
              <p className="font-barlow font-bold text-lg">{user?.name}</p>
              {currentTier && (
                <div className="flex items-center gap-2 mt-1">
                  <span 
                    className="px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ backgroundColor: currentTier.color }}
                  >
                    {currentTier.name}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-end justify-between">
              <div>
                <p className="text-white/70 text-xs mb-1">Número do Cartão</p>
                <div className="flex items-center gap-2">
                  <p className="font-barlow font-bold tracking-wider">{user?.loyalty_card_id}</p>
                  <button onClick={copyCardId} className="tap-target p-2 hover:bg-white/20 rounded" data-testid="copy-card-btn">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-xs mb-1">Pontos</p>
                <p className="font-barlow font-bold text-3xl">{currentPoints}</p>
                {loyaltyData?.points_value_aoa > 0 && (
                  <p className="text-white/80 text-xs">≈ {loyaltyData.points_value_aoa.toLocaleString()} AOA</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Points Progress */}
        {pointsToNextReward && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-barlow font-bold text-lg">Próxima Recompensa</h2>
              <Gift className="w-5 h-5 text-sonangol-orange" />
            </div>
            <div className="mb-3">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-zinc-600">{currentPoints} pontos</span>
                <span className="text-zinc-600">{pointsToNextReward.points} pontos</span>
              </div>
              <div className="h-3 bg-zinc-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-sonangol-red to-sonangol-orange rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, (currentPoints / pointsToNextReward.points) * 100)}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-xl">
              <div className="w-10 h-10 bg-sonangol-orange rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-barlow font-semibold text-zinc-800">{pointsToNextReward.name}</p>
                <p className="text-xs text-zinc-500">{pointsToNextReward.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* How it works */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-sonangol-red" />
            <h2 className="font-barlow font-bold text-lg">Como Funciona</h2>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-barlow font-bold text-sonangol-red">1</span>
              </div>
              <div>
                <p className="font-medium text-zinc-800">Compre Lubrificantes</p>
                <p className="text-xs text-zinc-500">Faça compras através da app HáLubri</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-barlow font-bold text-sonangol-orange">2</span>
              </div>
              <div>
                <p className="font-medium text-zinc-800">Acumule Pontos</p>
                <p className="text-xs text-zinc-500">1 ponto por cada 1.000 AOA gastos</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="font-barlow font-bold text-green-600">3</span>
              </div>
              <div>
                <p className="font-medium text-zinc-800">Troque por Prémios</p>
                <p className="text-xs text-zinc-500">Use os pontos para descontos e produtos grátis</p>
              </div>
            </div>
          </div>
        </div>

        {/* Available Rewards */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
          <h2 className="font-barlow font-bold text-lg mb-4">Recompensas Disponíveis</h2>
          <div className="space-y-3">
            {rewards.map((reward, idx) => {
              const canRedeem = currentPoints >= reward.points;
              return (
                <div 
                  key={idx} 
                  className={`flex items-center justify-between p-3 rounded-xl border ${
                    canRedeem ? 'border-green-200 bg-green-50' : 'border-zinc-200'
                  }`}
                  data-testid={`reward-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      canRedeem ? 'bg-green-500' : 'bg-zinc-200'
                    }`}>
                      <Gift className={`w-5 h-5 ${canRedeem ? 'text-white' : 'text-zinc-400'}`} />
                    </div>
                    <div>
                      <p className={`font-medium ${canRedeem ? 'text-green-700' : 'text-zinc-800'}`}>
                        {reward.name}
                      </p>
                      <p className="text-xs text-zinc-500">{reward.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-barlow font-bold ${canRedeem ? 'text-green-600' : 'text-zinc-600'}`}>
                      {reward.points}
                    </p>
                    <p className="text-xs text-zinc-400">pontos</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Points History */}
        {loyaltyData?.history && loyaltyData.history.length > 0 && (
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-barlow font-bold text-lg">Histórico de Pontos</h2>
              <History className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="space-y-3">
              {loyaltyData.history.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-800">Pedido #{item.order_id?.slice(0, 8)}</p>
                      <p className="text-xs text-zinc-500">
                        {new Date(item.date).toLocaleDateString('pt-AO', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                  <span className="text-green-600 font-bold">+{item.points}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logout Button */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-zinc-100">
          <Button
            variant="outline"
            className="w-full h-12 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            onClick={handleLogout}
            data-testid="loyalty-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Terminar Sessão
          </Button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default LoyaltyPage;
